import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  FieldValues
} from 'react-hook-form';
import { Icon } from '@takecasper/acuity-design-system';
import { IProgram, IUser } from '@types';
import { PERMISSIONS } from 'shared/enums';
import {
  ProgramFormElementWrapper,
  MultiSelectWrapper,
  IconWrapperStyled,
  ErrorTextStyled,
  stylesOverride
} from './create-account-program-select.styles';
import ProgramMultiSelect from '../program-multi-select/program-multi-select';
import { hasAdminRole } from 'shared/helpers/hasAdminRole';

interface ICreateAccountProgramSelectProps {
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  selectedPrograms: IProgram[];
  creator: IUser;
}

/**
 *
 * This component wraps the existing ProgramMultiSelect component and applies styling to match the
 * other elements on the Create Account form.
 *
 * @param errors - React Hook Form errors object
 * @param register - React Hook Form register function
 * @param setValue - React Hook Form setValue function
 * @param selectedPrograms - Array of selected programs
 * @param creator - The user object of the creator
 * @returns
 */
export const CreateAccountProgramSelect = ({
  errors,
  register,
  setValue,
  selectedPrograms,
  creator
}: ICreateAccountProgramSelectProps) => {
  const { t } = useTranslation();

  // Only include schools where the user doing the creation has Manage Users permission.
  // If the user is an admin, they can manage all schools, so no exclusions.
  const isAdmin = hasAdminRole(creator);
  const userSchoolsWithoutManageUsers = isAdmin
    ? []
    : creator?.schoolPermissions
        .filter(
          schoolPermission =>
            !schoolPermission.keys.includes(PERMISSIONS.MANAGE_USERS)
        )
        .map(schoolPermission => schoolPermission.schoolId);

  return (
    <ProgramFormElementWrapper>
      <MultiSelectWrapper>
        <ProgramMultiSelect
          id="create-account"
          excludedSchoolIds={userSchoolsWithoutManageUsers}
          styleOverride={stylesOverride(errors)}
          autoFocus={false}
          selectedPrograms={selectedPrograms}
          setSelectedPrograms={(programs: IProgram[]) =>
            setValue('programs', programs, {
              shouldDirty: true,
              shouldValidate: true
            })
          }
          {...register('programs', {
            validate: {
              required: value =>
                value?.length > 0 || t?.('input_program_required')
            }
          })}
        />
      </MultiSelectWrapper>
      {errors.programs && (
        <IconWrapperStyled>
          <Icon
            name="error"
            size="medium"
            className="ds-text-danger-red"
            data-testid="create_account_program_error_icon"
          />
        </IconWrapperStyled>
      )}
      <ErrorTextStyled>
        {errors.programs && (errors.programs.message as string)}
      </ErrorTextStyled>
    </ProgramFormElementWrapper>
  );
};
